import type { RequestConfig, RequestOptions } from '@@/plugin-request/types';
import { history, request } from '@umijs/max';
import { message, notification } from 'antd';
import { AuthRecoveryError, refreshAuthSession } from '@/utils/authApi';
import {
  clearAuthSession,
  createAuthorizationHeader,
  getAuthSession,
  getAuthSessionGeneration,
  isAccessTokenExpired,
} from '@/utils/authSession';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number | string;
  errorMessage?: string;
  uniformErrorCode?: number | string;
  uniformErrorMsg?: string;
  showType?: ErrorShowType;
}

type AuthAwareRequestOptions = RequestOptions & {
  authSkipAuthorization?: boolean;
  authSkipRefresh?: boolean;
  /** 允许请求前续期，但禁止已发送请求因认证失败被自动重放。 */
  authSkipRetry?: boolean;
  authRetry?: boolean;
  /** 请求层捕获的登录代次；认证重试必须仍属于同一次登录。 */
  authSessionGeneration?: number;
  /** 调用方接管权限拒绝反馈；不影响 401 或明确的令牌失效处理。 */
  handleForbiddenLocally?: boolean;
  url?: string;
};

type ApiRequestInterceptor = (
  url: string,
  config: AuthAwareRequestOptions,
) =>
| {
  url: string;
  options: AuthAwareRequestOptions;
}
| Promise<{
  url: string;
  options: AuthAwareRequestOptions;
}>;

const loginPath = '/user/login';

const AUTH_ERROR_CODES = new Set([
  '401',
  '100401',
  'AUTH_TOKEN_EXPIRED',
  'invalid_token',
  'AUTH_REFRESH_EXPIRED',
  'AUTH_USER_DISABLED',
  'UNAUTHORIZED',
]);

const FORBIDDEN_ERROR_CODES = new Set([
  '403',
  '100403',
  'FORBIDDEN',
  'AUTH_NO_PERMISSION',
]);

const redirectToLogin = () => {
  clearAuthSession();
  if (typeof window === 'undefined') {
    return;
  }

  const { pathname, search } = window.location;
  if (pathname === loginPath) {
    return;
  }

  history.replace(
    `${loginPath}?${new URLSearchParams({
      redirect: pathname + search,
    }).toString()}`,
  );
};

const isAuthErrorCode = (errorCode?: number | string) =>
  errorCode !== undefined && AUTH_ERROR_CODES.has(String(errorCode));

const getAuthErrorCode = (response?: Partial<ResponseStructure>) =>
  [response?.errorCode, response?.uniformErrorCode].find(isAuthErrorCode);

const isAuthFailureResponse = (response: any) => {
  const status = response?.status;
  const data = response?.data as Partial<ResponseStructure> | undefined;
  return status === 401 || Boolean(getAuthErrorCode(data));
};

const shouldHandleForbiddenLocally = (
  response: { status?: number; data?: Partial<ResponseStructure> } | undefined,
  config: AuthAwareRequestOptions | undefined,
) => {
  if (!config?.handleForbiddenLocally) return false;
  const codes = [response?.data?.errorCode, response?.data?.uniformErrorCode]
    .filter((code) => code !== undefined)
    .map(String);
  // 明确的未认证/令牌失效优先，不能被混合 HTTP 状态误认为权限不足。
  if (
    response?.status === 401 ||
    codes.some(
      (code) => AUTH_ERROR_CODES.has(code) && !FORBIDDEN_ERROR_CODES.has(code),
    )
  ) {
    return false;
  }
  return (
    response?.status === 403 ||
    codes.some((code) => FORBIDDEN_ERROR_CODES.has(code))
  );
};

const getBackendErrorMessage = (
  response?: Partial<ResponseStructure>,
  fallback = '请求失败',
) => response?.errorMessage || response?.uniformErrorMsg || fallback;

// 前后端同源：dev 由 umi devServer 代理转发，prod 由服务器统一网关分发，
// 一律使用相对路径 /wanApi /lanApi /bossApi。

const attachAuthorizationHeader = <T extends AuthAwareRequestOptions>(
  config: T,
): T => {
  if (config.authSkipAuthorization) {
    return config;
  }
  assertRequestSession(config);

  const authorization = createAuthorizationHeader();
  assertRequestSession(config);
  if (!authorization) {
    return config;
  }

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      Authorization: authorization,
    },
  };
};

const createAuthRedirectHandledError = () => {
  const error: any = new Error('登录状态已失效，请重新登录');
  error.authRedirectHandled = true;
  return error;
};

const assertRequestSession = (config: AuthAwareRequestOptions) => {
  if (
    !config.authSkipAuthorization &&
    config.authSessionGeneration !== undefined &&
    config.authSessionGeneration !== getAuthSessionGeneration()
  ) {
    throw Object.assign(new Error('登录会话已变化，请重新发起操作'), {
      authRedirectHandled: true,
    });
  }
};

const refreshRequestSession = async (config: AuthAwareRequestOptions) => {
  assertRequestSession(config);
  const refreshed = await refreshAuthSession();
  // 当前会话刷新失败会清理登录态；新账号的会话不能被旧请求清除。
  if (getAuthSession()) assertRequestSession(config);
  if (!refreshed) {
    redirectToLogin();
    return false;
  }
  assertRequestSession(config);
  return true;
};

const refreshBeforeRequestIfNeeded = async <T extends AuthAwareRequestOptions>(
  config: T,
): Promise<T> => {
  if (
    config.authSkipAuthorization ||
    config.authSkipRefresh ||
    config.authRetry ||
    !isAccessTokenExpired()
  ) {
    return config;
  }

  if (!(await refreshRequestSession(config))) throw createAuthRedirectHandledError();

  return config;
};

const retryRequest = (config: AuthAwareRequestOptions) => {
  if (!config.url) {
    return undefined;
  }

  return request(config.url, {
    ...attachAuthorizationHeader(config),
    authRetry: true,
    // 响应拦截器必须收到完整响应，最外层 request 再按调用方选项解包一次。
    getResponse: true,
  });
};

const refreshAndRetryRequest = async (config?: AuthAwareRequestOptions) => {
  if (
    !config ||
    config.authSkipRefresh ||
    config.authSkipRetry ||
    config.authRetry
  ) {
    return undefined;
  }

  if (!(await refreshRequestSession(config))) return undefined;

  return retryRequest(config);
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        data,
        errorCode,
        errorMessage,
        uniformErrorCode,
        uniformErrorMsg,
        showType,
      } = res as unknown as ResponseStructure;
      if (!success) {
        const displayMessage = getBackendErrorMessage({
          errorMessage,
          uniformErrorMsg,
        });
        const error: any = new Error(displayMessage);
        error.name = 'BizError';
        error.info = {
          errorCode,
          uniformErrorCode,
          errorMessage: displayMessage,
          showType,
          data,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      if (error.authRedirectHandled) {
        return;
      }
      if (error instanceof AuthRecoveryError) {
        message.error(error.message);
        return;
      }
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const errorCode = getAuthErrorCode(errorInfo) || errorInfo.errorCode;
          const displayMessage = getBackendErrorMessage(errorInfo);
          if (getAuthErrorCode(errorInfo)) {
            message.error(displayMessage || '登录状态已失效，请重新登录');
            redirectToLogin();
            return;
          }
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(displayMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(displayMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: displayMessage,
                message: errorCode || '请求失败',
              });
              break;
            case ErrorShowType.REDIRECT:
              redirectToLogin();
              break;
            default:
              message.error(displayMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (isAuthFailureResponse(error.response)) {
          message.error('登录状态已失效，请重新登录');
          redirectToLogin();
          return;
        }
        message.error(
          getBackendErrorMessage(
            error.response.data as Partial<ResponseStructure> | undefined,
            `请求失败：${error.response.status}`,
          ),
        );
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // `error.request` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (async (url: string, config: AuthAwareRequestOptions) => ({
      url,
      options: attachAuthorizationHeader(
        await refreshBeforeRequestIfNeeded({
          ...config,
          authSessionGeneration:
            config.authSessionGeneration ?? getAuthSessionGeneration(),
        }),
      ),
    })) as ApiRequestInterceptor,
  ],

  // 响应拦截器
  responseInterceptors: [
    [
      async (response: any) => {
        const config = response?.config as AuthAwareRequestOptions | undefined;

        if (
          !shouldHandleForbiddenLocally(response, config) &&
          isAuthFailureResponse(response)
        ) {
          const retryResponse = await refreshAndRetryRequest(config);
          if (retryResponse) {
            return retryResponse;
          }
        }

        return response;
      },
      async (error: any) => {
        const config = error.config as AuthAwareRequestOptions | undefined;
        if (
          !shouldHandleForbiddenLocally(error.response, config) &&
          isAuthFailureResponse(error.response)
        ) {
          const retryResponse = await refreshAndRetryRequest(config);
          if (retryResponse) {
            return retryResponse;
          }
          // eslint-disable-next-line require-atomic-updates, no-param-reassign -- The interceptor must mark the same rejected error; this flag only transitions to true and prevents duplicate auth feedback.
          error.authRedirectHandled = true;
        }
        return Promise.reject(error);
      },
    ] as any,
  ],
};
